import React, { useState, useEffect } from 'react';
import { getFirestore, collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc, query, orderBy, writeBatch } from "firebase/firestore";
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { Modal, Button, Spinner, Form, Nav, ButtonGroup, ProgressBar } from 'react-bootstrap';
import { FaEdit, FaTrash, FaUndo, FaFolderOpen, FaTimesCircle, FaCloudUploadAlt, FaSave, FaYoutube, FaEye } from 'react-icons/fa';
import './xDocList.css';

const AdminConsole = () => {
    const [activeSection, setActiveSection] = useState("links");
    const [links, setLinks] = useState([]);
    const [videos, setVideos] = useState([]);
    const [artists, setArtists] = useState([]);
    const [viewTab, setViewTab] = useState("active");
    const [showModal, setShowModal] = useState(false);

    const [formData, setFormData] = useState({
        title: "", url: "", bio: "", date: "", externalLink: "", 
        files: [], file: null, existingFiles: [], existingUrl: "", existingPath: "", linkType: "file" 
    });
    
    const [editingId, setEditingId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const db = getFirestore();
    const storage = getStorage();

    useEffect(() => {
        const unsubLinks = onSnapshot(query(collection(db, "xLinkz"), orderBy("createdAt", "desc")), (snap) => setLinks(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubVideos = onSnapshot(query(collection(db, "xVideoz"), orderBy("createdAt", "desc")), (snap) => setVideos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubArtists = onSnapshot(query(collection(db, "xArtistz"), orderBy("createdAt", "desc")), (snap) => setArtists(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        return () => { unsubLinks(); unsubVideos(); unsubArtists(); };
    }, [db]);

    const getYouTubeId = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const getFileNameFromUrl = (url) => {
        if (!url) return "Attachment";
        try {
            const decodedUrl = decodeURIComponent(url);
            const match = decodedUrl.match(/\/([^/?]+)\?|(?:\/)([^/?]+)$/);
            const rawName = match ? (match[1] || match[2]) : "File";
            return rawName.includes('_') ? rawName.split('_').slice(1).join('_') : rawName;
        } catch (e) { return "File"; }
    };

    const deleteFileFromStorage = async (path) => {
        if (!path) return;
        try { 
            const fileRef = ref(storage, path);
            await deleteObject(fileRef); 
        } catch (e) { console.error("Storage Error:", e); }
    };

    const handleSave = async () => {
        if (!formData.title) return alert("Title is required");
        setIsProcessing(true);
        setUploadProgress(0);
        
        const col = activeSection === "links" ? "xLinkz" : activeSection === "videos" ? "xVideoz" : "xArtistz";
        let data = { 
            title: formData.title, updatedAt: new Date(), status: 'active',
            ...(activeSection === "artists" && { bio: formData.bio, performanceDate: formData.date, externalLink: formData.externalLink })
        };

        try {
            const uploadFile = async (file, folder) => {
                const uniqueName = `${Date.now()}_${file.name}`;
                const sRef = ref(storage, `${folder}/${uniqueName}`);
                const task = uploadBytesResumable(sRef, file);
                return new Promise((resolve, reject) => {
                    task.on('state_changed', 
                        (snap) => setUploadProgress((snap.bytesTransferred / snap.totalBytes) * 100),
                        reject,
                        async () => resolve({ url: await getDownloadURL(task.snapshot.ref), path: sRef.fullPath })
                    );
                });
            };

            if (activeSection === "links") {
                if (formData.linkType === "file") {
                    if (formData.file) {
                        if (formData.existingPath) await deleteFileFromStorage(formData.existingPath);
                        const result = await uploadFile(formData.file, "xLinkz");
                        data.fileUrl = result.url;
                        data.filePath = result.path;
                        data.externalUrl = "";
                    } else {
                        data.fileUrl = formData.existingUrl;
                        data.filePath = formData.existingPath;
                        data.externalUrl = "";
                    }
                } else {
                    if (formData.existingPath) await deleteFileFromStorage(formData.existingPath);
                    data.externalUrl = formData.url;
                    data.fileUrl = "";
                    data.filePath = "";
                }
            } else if (activeSection === "artists") {
                if (formData.file) {
                    if (formData.existingPath) await deleteFileFromStorage(formData.existingPath);
                    const result = await uploadFile(formData.file, "xArtistz");
                    data.fileUrl = result.url;
                    data.filePath = result.path;
                } else {
                    data.fileUrl = formData.existingUrl;
                    data.filePath = formData.existingPath;
                }
            } else if (activeSection === "videos") {
                data.fileUrl = formData.url;
            }

            if (editingId) { await updateDoc(doc(db, col, editingId), data); } 
            else { 
                data.createdAt = new Date(); 
                data.isFeatured = false;
                if(activeSection === "artists") data.isActive = true; 
                await addDoc(collection(db, col), data); 
            }
            closeModal();
        } catch (e) { console.error(e); alert("Save Failed"); } finally { setIsProcessing(false); }
    };

    const handleDeleteRecord = async (item) => {
        if (!window.confirm("Permanently delete this record?")) return;
        const col = activeSection === "links" ? "xLinkz" : activeSection === "videos" ? "xVideoz" : "xArtistz";
        try {
            if (item.filePath) await deleteFileFromStorage(item.filePath);
            await deleteDoc(doc(db, col, item.id));
        } catch (e) { console.error(e); }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setUploadProgress(0);
        setFormData({ title: "", url: "", bio: "", date: "", externalLink: "", files: [], file: null, existingFiles: [], existingUrl: "", existingPath: "", linkType: "file" });
    };

    const currentData = activeSection === "links" ? links : activeSection === "videos" ? videos : artists;
    const filteredData = currentData.filter(item => viewTab === "archived" ? item.status === "archived" : item.status !== "archived");

    const labelStyle = { fontSize: '0.65rem', fontWeight: '600', marginTop: '5px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' };

    return (
        <div className="admin-mobile-console p-2 text-start">
            <div className="mb-3">
                <ButtonGroup className="w-100 shadow-sm border border-secondary border-opacity-25 rounded overflow-hidden">
                    {["links", "videos", "artists"].map(sec => (
                        <Button key={sec} variant={activeSection === sec ? "info" : "dark"} className="text-uppercase py-2 small fw-bold" onClick={() => setActiveSection(sec)}>
                            {sec}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            <Button variant="outline-success" className="mb-3 w-100 fw-bold py-2 shadow-sm" onClick={() => setShowModal(true)}>+ NEW {activeSection.toUpperCase()}</Button>
            
            <Nav variant="pills" className="admin-tabs-mobile mb-3" activeKey={viewTab} onSelect={k => setViewTab(k)}>
                <Nav.Item className="flex-fill text-center"><Nav.Link eventKey="active">Active</Nav.Link></Nav.Item>
                <Nav.Item className="flex-fill text-center"><Nav.Link eventKey="archived">Archived</Nav.Link></Nav.Item>
            </Nav>

            <div className="admin-list-container">
                {filteredData.map(item => (
                    <div key={item.id} className="doc-card p-0 mb-3 rounded border border-secondary bg-dark bg-opacity-25 overflow-hidden shadow-sm">
                        <div className="p-3 border-bottom border-secondary border-opacity-25 d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                                {(item.fileUrl && (activeSection === "artists" || activeSection === "links")) && <img src={item.fileUrl} style={{width:'35px', height:'35px', objectFit:'cover', borderRadius:'4px'}} alt="" />}
                                <div>
                                    <div className="fw-bold">{item.title}</div>
                                    {activeSection === "artists" && (
                                        <div className="smallest text-info">
                                            {item.isFeatured ? "★ Featured Spotlight" : (item.isActive ? "● Live" : "○ Hidden")}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="d-flex align-items-center gap-3">
                                {activeSection !== "links" && viewTab !== "archived" && (
                                    <div className="text-center">
                                        <div className="smallest text-muted mb-1">SPOTLIGHT</div>
                                        <Form.Check type="switch" checked={item.isFeatured} onChange={() => {
                                            const batch = writeBatch(db);
                                            const colName = activeSection === "videos" ? "xVideoz" : "xArtistz";
                                            (activeSection === "videos" ? videos : artists).forEach(i => batch.update(doc(db, colName, i.id), { isFeatured: i.id === item.id }));
                                            batch.commit();
                                        }} />
                                    </div>
                                )}
                                {activeSection === "artists" && viewTab !== "archived" && (
                                    <div className="text-center">
                                        <div className="smallest text-muted mb-1">LIST</div>
                                        <Form.Check type="switch" checked={item.isActive} onChange={() => {
                                            updateDoc(doc(db, "xArtistz", item.id), { isActive: !item.isActive });
                                        }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="d-flex p-3 gap-4 justify-content-center">
                            <div className="d-flex flex-column align-items-center">
                                <Button variant="outline-light" className="circle-btn white" onClick={() => { 
                                    setEditingId(item.id); 
                                    setFormData({ 
                                        title: item.title, url: item.fileUrl || item.externalUrl || "", 
                                        bio: item.bio || "", date: item.performanceDate || "", externalLink: item.externalLink || "", 
                                        existingUrl: item.fileUrl || "", existingPath: item.filePath || "",
                                        linkType: (activeSection === "links" && item.externalUrl) ? "url" : "file", files: [], file: null 
                                    }); 
                                    setShowModal(true); 
                                }}><FaEdit /></Button>
                                <span style={labelStyle}>EDIT</span>
                            </div>

                            <div className="d-flex flex-column align-items-center">
                                <Button variant="outline-warning" className="circle-btn" onClick={() => updateDoc(doc(db, activeSection === "links" ? "xLinkz" : (activeSection === "videos" ? "xVideoz" : "xArtistz"), item.id), { status: item.status === 'archived' ? 'active' : 'archived' })}>
                                    {item.status === 'archived' ? <FaUndo className="blue" /> : <FaFolderOpen className="blue" />}
                                </Button>
                                <span style={labelStyle}>{item.status === 'archived' ? 'RESTORE' : 'ARCHIVE'}</span>
                            </div>

                            <div className="d-flex flex-column align-items-center">
                                <Button variant="danger" className="circle-btn btn-delete" onClick={() => handleDeleteRecord(item)}>
                                    <FaTrash className="danger" />
                                </Button>
                                <span style={labelStyle}>DELETE</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Modal show={showModal} onHide={closeModal} centered contentClassName="bg-dark border border-secondary shadow-lg">
                <Modal.Header closeButton closeVariant="white">
                    <Modal.Title className="fw-bold">{editingId ? "Update" : "Add"} {activeSection.toUpperCase()}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 bg-dark">
                    <Form.Group className="mb-4">
                        <Form.Label className="text-info smallest fw-bold">1. ENTER TITLE</Form.Label>
                        <Form.Control className="bg-dark glass-input" placeholder="Descriptive title..." value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                    </Form.Group>

                    {activeSection === "links" && (
                        <ButtonGroup className="w-100 mb-4 shadow-sm">
                            <Button variant={formData.linkType === "file" ? "info" : "outline-secondary"} onClick={() => setFormData({...formData, linkType: "file"})} className="py-2">Local File</Button>
                            <Button variant={formData.linkType === "url" ? "info" : "outline-secondary"} onClick={() => setFormData({...formData, linkType: "url"})} className="py-2">Web Link</Button>
                        </ButtonGroup>
                    )}

                    {activeSection === "artists" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-info smallest fw-bold">WEBSITE / EXTERNAL LINK</Form.Label>
                                <Form.Control className=" bg-dark glass-input" placeholder="https://..." value={formData.externalLink} onChange={e => setFormData({...formData, externalLink: e.target.value})} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-info smallest fw-bold">PERFORMANCE DATES</Form.Label>
                                <Form.Control className="bg-dark glass-input" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="text-info smallest fw-bold">BIO</Form.Label>
                                <Form.Control as="textarea" rows={3} className="bg-dark glass-input" value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})} />
                            </Form.Group>
                        </>
                    )}

                    <Form.Group className="mb-4">
                        <Form.Label className="text-info smallest fw-bold">
                            {activeSection === "videos" ? "2. YOUTUBE URL" : "2. SELECT MEDIA"}
                        </Form.Label>
                        
                        <div className="mb-3 smallest text-white-50 fst-italic">
                            {activeSection === "videos" ? "Paste the full YouTube link below to preview." : (formData.linkType === "file" ? "Attach one document below, then press Save to upload." : "Enter a valid website URL below.")}
                        </div>

                        {activeSection === "videos" && (
                            <>
                                <Form.Control className="bg-dark glass-input mb-3" placeholder="https://youtube.com/watch?v=..." value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} />
                                {getYouTubeId(formData.url) && (
                                    <div className="preview-gallery text-center">
                                        <div className="smallest text-info fw-bold mb-2 d-flex align-items-center justify-content-center gap-2">IMAGE PREVIEW</div>
                                        <img src={`https://img.youtube.com/vi/${getYouTubeId(formData.url)}/mqdefault.jpg`} className="img-fluid rounded border border-secondary" alt="thumbnail" />
                                    </div>
                                )}
                            </>
                        )}

                        {(activeSection === "links" || activeSection === "artists") && (
                            formData.linkType === "url" && activeSection === "links" ? (
                                <Form.Control className="bg-dark glass-input" placeholder="https://..." value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} />
                            ) : (
                                <div className="preview-gallery mb-3">
                                    <div className="smallest text-info fw-bold mb-2 d-flex align-items-center gap-2"><FaEye /> PREVIEW FILE</div>
                                    {formData.existingUrl ? (
                                        <div className="d-flex align-items-center gap-3 p-2 bg-black bg-opacity-25 rounded">
                                            {(formData.existingUrl.match(/\.(jpeg|jpg|gif|png)$/) || activeSection === "artists") ? 
                                                <img src={formData.existingUrl} style={{height: '60px', width: '60px', objectFit: 'cover', borderRadius: '4px'}} alt="" /> :
                                                <div className="bg-secondary rounded d-flex align-items-center justify-content-center" style={{height: '60px', width: '60px'}}><FaFolderOpen /></div>
                                            }
                                            <span className="small flex-grow-1 text-truncate text-white-50">{getFileNameFromUrl(formData.existingUrl)}</span>
                                            <FaTimesCircle className="text-danger cursor-pointer" onClick={() => setFormData({...formData, existingUrl: "", existingPath: ""})} />
                                        </div>
                                    ) : (
                                        formData.file ? (
                                            <div className="d-flex align-items-center gap-3 p-2 bg-info bg-opacity-10 rounded">
                                                {formData.file.type.startsWith('image/') ? 
                                                    <img src={URL.createObjectURL(formData.file)} style={{height: '60px', width: '60px', objectFit: 'cover', borderRadius: '4px'}} alt="" /> :
                                                    <div className="bg-info bg-opacity-25 rounded d-flex align-items-center justify-content-center" style={{height: '60px', width: '60px'}}><FaCloudUploadAlt /></div>
                                                }
                                                <span className="small flex-grow-1 text-truncate text-info">NEW: {formData.file.name}</span>
                                                <FaTimesCircle className="text-danger cursor-pointer" onClick={() => setFormData({...formData, file: null})} />
                                            </div>
                                        ) : (
                                            <div className="custom-file-upload border border-secondary border-dashed rounded p-4 text-center bg-dark" style={{cursor:'pointer'}}>
                                                <Form.Control type="file" className="d-none" id="file-input" onChange={e => setFormData({...formData, file: e.target.files[0]})} />
                                                <label htmlFor="file-input" className="m-0 small text-white-50 w-100 cursor-pointer d-flex flex-column align-items-center gap-2">
                                                    <FaCloudUploadAlt size={24} className="text-info" />
                                                    Select {activeSection === "artists" ? "Artist Image" : "File to Upload"}
                                                </label>
                                            </div>
                                        )
                                    )}
                                </div>
                            )
                        )}
                    </Form.Group>

                    {isProcessing && uploadProgress > 0 && (
                        <div className="mb-4">
                            <div className="smallest text-info text-center mb-1 fw-bold">UPLOADING... {Math.round(uploadProgress)}%</div>
                            <ProgressBar now={uploadProgress} animated variant="info" style={{height:'8px'}} />
                        </div>
                    )}

                    <Button variant="info" className="w-100 fw-bold py-3 shadow-sm d-flex align-items-center justify-content-center gap-2" style={{cursor:'pointer'}} onClick={handleSave} disabled={isProcessing}>
                        {isProcessing ? <Spinner size="sm" /> : <><FaSave /> 3. SAVE {activeSection.toUpperCase()}</>}
                    </Button>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AdminConsole;